import { createContext, useCallback, useMemo, useRef } from "react";
import type { ReactElement, ReactNode } from "react";
import { Box } from "@chakra-ui/react";
import { call } from "goi_common";
import { UrgentCallBanner } from "goi_common/src/design-system";

import { useCustomVariation } from "@/hooks/useCustomVariation";
import { useParlorInquiryModal } from "@/hooks/useParlorInquiryModal";

/**
 * now 콜 확대 실험. B안만 첫 화면에 임종 배너를 띄운다. 페이지마다 실험을 나눈다(2026-10-13 영민).
 * 0 이면 Hackle 미발급이라 훅을 부르지 않고 A 화면(fallback)만 그린다.
 */
export const URGENT_CALL_BANNER_KEYS = {
  parlor_match_request: 358, // 식장 견적 /match/parlor-requests/[id]
  match_request: 359, // 견적 직행 /match/request
} as const;

interface UrgentCallBannerState {
  /** 배너가 B안으로 떠 있는지. 견적 직행 첫 질문이 맨 위 80px 여백을 줄이는 데 쓴다 */
  isShown: boolean;
  /**
   * 배너 바로 아래 스크롤 위치. 배너가 떠 있고 사용자가 퍼널을 한 번이라도 눌렀을 때만 값이 있다.
   * 견적 직행은 단계마다 맨 위로 스크롤해서 배너가 계속 보였다. 식장 견적처럼 두 번째 화면부터는
   * 스크롤로 배너를 지나가게 하려고 둔다. 첫 화면(아직 아무것도 안 누름)에서는 null 이라 배너가 보인다.
   */
  getScrollTopPastBanner: () => number | null;
}

export const UrgentCallBannerContext = createContext<UrgentCallBannerState>({
  isShown: false,
  getScrollTopPastBanner: () => null,
});

interface UrgentCallBannerExperimentProps {
  eventPath: "parlor_match_request" | "match_request";
  placeName?: string;
  /** 배너를 감쌀 여백. 페이지마다 첫 화면 레이아웃이 달라 호출부가 정한다 */
  padding: string;
  /** A안(현행)에서 그 자리에 원래 있던 것 */
  fallback?: ReactElement;
  /** 배너 아래에 이어지는 화면. 배너 노출 여부를 context 로 받는다 */
  children?: ReactNode;
}

export const UrgentCallBannerExperiment = (props: UrgentCallBannerExperimentProps) => {
  if (URGENT_CALL_BANNER_KEYS[props.eventPath] <= 0) {
    return (
      <>
        {props.fallback}
        {props.children}
      </>
    );
  }
  return <UrgentCallBannerGate {...props} />;
};

const UrgentCallBannerGate = ({
  eventPath,
  placeName,
  padding,
  fallback,
  children,
}: UrgentCallBannerExperimentProps) => {
  const variation = useCustomVariation(URGENT_CALL_BANNER_KEYS[eventPath]);
  // Z(미할당) 는 A 와 같이 현행을 그린다. 빈 화면을 만들면 Hackle 에서 실험을 켜기 전 구간에 자리가 비어 보인다.
  const isShown = variation === "B";
  const bannerRef = useRef<HTMLDivElement>(null);
  const hasInteractedRef = useRef(false);

  const getScrollTopPastBanner = useCallback(() => {
    const el = bannerRef.current;
    if (!isShown || !hasInteractedRef.current || !el) return null;
    return el.getBoundingClientRect().bottom + window.scrollY;
  }, [isShown]);

  const state = useMemo(
    () => ({ isShown, getScrollTopPastBanner }),
    [isShown, getScrollTopPastBanner],
  );

  return (
    <UrgentCallBannerContext.Provider value={state}>
      {isShown ? (
        <div ref={bannerRef}>
          <BannerBody eventPath={eventPath} placeName={placeName} padding={padding} />
        </div>
      ) : (
        fallback
      )}
      {/* display: contents 라 레이아웃은 그대로. 퍼널 안 첫 터치만 기록한다 */}
      <div
        style={{ display: "contents" }}
        onPointerDownCapture={() => {
          hasInteractedRef.current = true;
        }}
      >
        {children}
      </div>
    </UrgentCallBannerContext.Provider>
  );
};

const BannerBody = ({
  eventPath,
  placeName,
  padding,
}: Pick<UrgentCallBannerExperimentProps, "eventPath" | "placeName" | "padding">) => {
  // 모달 안 선택지 이벤트(ask-*)는 urgent_bnr path 로 쏴서 헤더 24시간 버튼 모달과 나눈다.
  // 견적 직행에서는 모달을 열지 않아 쓰이지 않는다.
  const { openParlorInquiryModal } = useParlorInquiryModal("urgent_bnr");

  return (
    <Box width="100%" padding={padding}>
      <UrgentCallBanner
        placeName={placeName}
        eventPath={eventPath}
        onCall={() => {
          // 식장 견적: 헤더 24시간 버튼과 같은 문의 유형 모달로 조문객을 장례식장 직통으로 거른다.
          // 견적 직행: 장례식장이 정해지지 않아 조문 문의를 보낼 번호가 없다(헤더와 같은 판단). 바로 고이로 건다.
          if (eventPath === "parlor_match_request") {
            void openParlorInquiryModal();
            return;
          }
          // isUrgent: 새벽 2~6시 alert 로 막지 않는다. 그 시간 임종 전화가 이 배너의 대상이다.
          call("1666-9784", true);
        }}
      />
    </Box>
  );
};
